import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { examsApi } from "@/api/exams";
import { attemptsApi } from "@/api/attempts";
import { authApi } from "@/api/auth";
import { categoriesApi } from "@/api/categories";
import type { SubmitAnswerRequest } from "@/types";

export function useExams(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: ["exams", page, pageSize],
    queryFn: () => examsApi.getAll(page, pageSize),
  });
}

export function useExam(id: string) {
  return useQuery({
    queryKey: ["exams", id],
    queryFn: () => examsApi.getById(id),
    enabled: !!id,
  });
}

export function useStartExam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (examId: string) => attemptsApi.start(examId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attempts"] });
    },
  });
}

export function useSubmitAnswer(attemptId: string) {
  return useMutation({
    mutationFn: (data: SubmitAnswerRequest) => attemptsApi.submitAnswer(attemptId, data),
  });
}

export function useCompleteExam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attemptId: string) => attemptsApi.complete(attemptId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attempts"] });
    },
  });
}

export function useAttemptHistory(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: ["attempts", "history", page, pageSize],
    queryFn: () => attemptsApi.getHistory(page, pageSize),
  });
}

export function useAttemptDetail(attemptId: string) {
  return useQuery({
    queryKey: ["attempts", attemptId],
    queryFn: () => attemptsApi.getById(attemptId),
    enabled: !!attemptId,
  });
}

export function useAttemptReview(attemptId: string) {
  return useQuery({
    queryKey: ["attempts", attemptId, "review"],
    queryFn: () => attemptsApi.getReview(attemptId),
    enabled: !!attemptId,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: () => categoriesApi.getAll(),
  });
}

export function useCategory(slug: string) {
  return useQuery({
    queryKey: ["categories", slug],
    queryFn: () => categoriesApi.getBySlug(slug),
    enabled: !!slug,
  });
}

export function useCategoryQuestions(slug: string, page = 1, pageSize = 20) {
  return useQuery({
    queryKey: ["categories", slug, "questions", page, pageSize],
    queryFn: () => categoriesApi.getQuestions(slug, page, pageSize),
    enabled: !!slug,
  });
}

export function useUserStats() {
  return useQuery({
    queryKey: ["auth", "stats"],
    queryFn: () => authApi.getStats(),
  });
}
